import { useEffect, useRef, useState } from 'react';
import { cn } from '../../../helpers/commonUtils';
import styles from './DropDownMenu.module.css';
import type { DropDownMenuItem, DropDownMenuProps } from './types';

function resolveInitialValue(
    items: DropDownMenuItem[],
    preferredValue?: string
): string {
    if (preferredValue && items.some((item) => item.value === preferredValue)) {
        return preferredValue;
    }
    return items[0]?.value ?? '';
}

function ItemMedia({ item }: { item: DropDownMenuItem }) {
    if (item.icon) {
        return <span className={styles.icon}>{item.icon}</span>;
    }

    return <span className={styles.itemSymbol}>{item.symbol || ''}</span>;
}

function ButtonMedia({ item }: { item: DropDownMenuItem }) {
    if (item.icon) {
        return <span className={styles.icon}>{item.icon}</span>;
    }

    return item.symbol || item.label || item.value;
}

export function DropDownMenu({
    items,
    value,
    onChange,
    className,
    ariaLabel,
}: DropDownMenuProps) {
    const rootRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [internalValue, setInternalValue] = useState(() =>
        resolveInitialValue(items, value)
    );

    const selectedValue = value ?? internalValue;
    const selectedItem = items.find((item) => item.value === selectedValue) ?? items[0];

    useEffect(() => {
        if (value !== undefined) {
            setInternalValue(resolveInitialValue(items, value));
        }
    }, [items, value]);

    useEffect(() => {
        function handleDocumentClick(event: MouseEvent) {
            if (!rootRef.current?.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }

        function handleKeyDown(event: KeyboardEvent) {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        }

        document.addEventListener('click', handleDocumentClick);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('click', handleDocumentClick);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    if (items.length === 0 || !selectedItem) {
        return null;
    }

    const handleSelect = (nextValue: string) => {
        if (value === undefined) {
            setInternalValue(nextValue);
        }
        setIsOpen(false);
        onChange?.(nextValue);
    };

    return (
        <div
            ref={rootRef}
            className={cn(styles.root, isOpen && styles.isOpen, className)}
        >
            <button
                type="button"
                className={styles.button}
                onClick={() => setIsOpen((open) => !open)}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-label={ariaLabel}
            >
                <span className={styles.buttonSymbol}>
                    <ButtonMedia item={selectedItem} />
                </span>
                <span className={styles.arrow} aria-hidden="true" />
            </button>

            <div className={styles.list} role="listbox">
                {items.map((item) => {
                    const isSelected = item.value === selectedValue;

                    return (
                        <button
                            key={item.value}
                            type="button"
                            role="option"
                            aria-selected={isSelected}
                            className={cn(styles.item, isSelected && styles.itemSelected)}
                            onClick={() => handleSelect(item.value)}
                        >
                            {(item.symbol || item.icon) ? <ItemMedia item={item} /> : null}
                            <span className={styles.itemLabel}>{item.label || item.value}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
